import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../common/AnimatedSection";

const LeadershipMessage = ({ data = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Ensure data is always safe
  const safeData = Array.isArray(data) ? data : [];

  // Reset index if data changes
  useEffect(() => {
    setActiveIndex(0);
  }, [safeData]);

  // Auto slider
  useEffect(() => {
    if (safeData.length === 0) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) =>
        prev === safeData.length - 1 ? 0 : prev + 1
      );
    }, 6000);

    return () => clearInterval(interval);
  }, [safeData]);

  // Prevent crash before data loads
  if (safeData.length === 0) return null;

  const current = safeData[activeIndex];

  return (
    <section className="py-24 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        <AnimatedSection>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary/[0.06] text-primary text-xs font-bold rounded-full tracking-widest uppercase">
              Leadership
            </span>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.1}>
          <div className="relative">

            <div className="relative bg-white rounded-3xl shadow-2xl shadow-dark/[0.06] border border-dark/[0.04] overflow-hidden">

              {/* Progress bar */}
              <motion.div
                key={`bar-${activeIndex}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 6, ease: "linear" }}
                className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary via-primary/80 to-primary/40 origin-left z-10"
              />

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.6 }}
                  className="grid lg:grid-cols-5"
                >

                  {/* PROFILE */}
                  <div className="lg:col-span-2 p-10 md:p-14 flex flex-col items-center justify-center bg-gradient-to-br from-primary/[0.03]">

                    <div className="w-44 h-44 rounded-full bg-primary/[0.08] flex items-center justify-center ring-[6px] ring-white shadow-xl overflow-hidden">

                      <img
                        src={current?.image || "/placeholder.png"}
                        alt={current?.name || "Leader"}
                        className="w-full h-full object-cover"
                      />

                    </div>

                    <div className="text-center mt-8">
                      <h3 className="text-xl font-bold text-dark">
                        {current?.name}
                      </h3>

                      <p className="text-sm text-primary font-semibold mt-1">
                        {current?.designation}
                      </p>

                      <p className="text-xs text-dark/30 mt-2">
                        {current?.years_experience}+ Years Experience
                      </p>
                    </div>

                  </div>

                  {/* CONTENT */}
                  <div className="lg:col-span-3 p-10 md:p-14 lg:p-16 flex flex-col justify-center">

                    <h2 className="text-sm font-bold text-primary tracking-widest uppercase mb-6">
                      Message from Leadership
                    </h2>

                    <blockquote className="text-xl md:text-2xl text-dark/75 font-light italic leading-relaxed">
                      {current?.message_title}
                    </blockquote>

                    <p className="mt-8 text-dark/60 leading-relaxed">
                      {current?.message}
                    </p>

                    {/* STATS */}
                    <div className="mt-10 flex flex-wrap gap-6 pt-8 border-t border-dark/10">

                      <div>
                        <div className="text-xl font-extrabold text-primary">
                          {current?.years_experience || 0}+
                        </div>
                        <div className="text-xs text-dark/40">
                          Years Experience
                        </div>
                      </div>

                      <div>
                        <div className="text-xl font-extrabold text-primary">
                          {current?.lives_affected || 0}+
                        </div>
                        <div className="text-xs text-dark/40">
                          Lives Impacted
                        </div>
                      </div>

                      <div>
                        <div className="text-xl font-extrabold text-primary">
                          {current?.partnerships || 0}+
                        </div>
                        <div className="text-xs text-dark/40">
                          Partnerships
                        </div>
                      </div>

                    </div>

                  </div>

                </motion.div>
              </AnimatePresence>

            </div>

            {/* DOTS */}
            <div className="flex justify-center gap-2 mt-8">
              {safeData.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    activeIndex === i
                      ? "w-8 bg-primary"
                      : "w-2 bg-dark/10"
                  }`}
                />
              ))}
            </div>

          </div>
        </AnimatedSection>

      </div>
    </section>
  );
};

export default LeadershipMessage;